import { CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { Space, Tabs, Card, Tooltip, Typography, Grid } from 'antd';
import Dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { type TFunction, useTranslation } from 'react-i18next';
import { type CurrentUserDataFragment } from '../../../api/fragments/CurrentUserData';
import { useAccount } from '../../../components/contexts/AccountContextManager';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import UserSelfGeneralSettings from './General/UserSelfGeneralSettings';
import UserSelfAuthenticatorSettings from './UserSelfAuthenticatorSettings';
import UserSelfPasswordSettings from './UserSelfPasswordSettings';
import UserSelfSessions from './UserSelfSessions';
import UserSelfWebAuthnSettings from './UserSelfWebAuthnSettings';

let isUserVerifyingPlatformAuthenticatorAvailable = false;

const getAuthenticatorTabTitle = (t: TFunction, isAuthenticatorEnabled: boolean) => {
    if (isAuthenticatorEnabled) {
        return (
            <Space>
                {t('userSelf:authenticatorSettings.title')}
                <Tooltip title={t('userSelf:authenticatorSettings.badge.enabled')}>
                    <Typography.Text type="success">
                        <CheckCircleOutlined />
                    </Typography.Text>
                </Tooltip>
            </Space>
        );
    }

    return (
        <Space>
            {t('userSelf:authenticatorSettings.title')}
            <Tooltip title={t('userSelf:authenticatorSettings.badge.disabled')}>
                <Typography.Text type="danger">
                    <WarningOutlined />
                </Typography.Text>
            </Tooltip>
        </Space>
    );
};

const getPasswordTabTitle = (t: TFunction, user: CurrentUserDataFragment) => {
    if (user.isPasswordExpired) {
        return (
            <Space>
                {t('userSelf:passwordSettings.title')}
                <Tooltip title={t('userSelf:passwordSettings.badge.expired')}>
                    <Typography.Text type="danger">
                        <WarningOutlined />
                    </Typography.Text>
                </Tooltip>
            </Space>
        );
    }

    const isPasswordNearExpiration = Dayjs(user.passwordExpiresAt).subtract(10, 'day').isBefore(new Date());

    if (isPasswordNearExpiration) {
        return (
            <Space>
                {t('userSelf:passwordSettings.title')}
                <Tooltip title={t('userSelf:passwordSettings.badge.nearExpiration')}>
                    <Typography.Text type="warning">
                        <WarningOutlined />
                    </Typography.Text>
                </Tooltip>
            </Space>
        );
    }

    const remainingValidity = Dayjs.duration(Dayjs(user.passwordExpiresAt).diff(new Date())).days();

    return (
        <Space>
            {t('userSelf:passwordSettings.title')}
            <Tooltip title={t('userSelf:passwordSettings.badge.valid', { days: remainingValidity })}>
                <Typography.Text type="success">
                    <CheckCircleOutlined />
                </Typography.Text>
            </Tooltip>
        </Space>
    );
};

const UserSelfPage = () => {
    const { t } = useTranslation('userSelf');
    const [mayWebAuthn, setMayWebAuthn] = useState(isUserVerifyingPlatformAuthenticatorAvailable);
    const user = useAccount();

    useEffect(() => {
        if (PublicKeyCredential) {
            PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable().then(result => {
                isUserVerifyingPlatformAuthenticatorAvailable = result;
                setMayWebAuthn(result);
            });
        }
    }, [setMayWebAuthn]);

    const screens = Grid.useBreakpoint();

    return (
        <ConsolePageWithHeader subTitle={t('userSelf:subTitle')} title={t('userSelf:title')}>
            <Card>
                <Tabs tabPosition={screens.lg ? 'left' : 'top'}>
                    <Tabs.TabPane
                        key="UserSelfGeneralSettings"
                        tab={t('userSelf:general:title')}
                        destroyInactiveTabPane
                    >
                        <UserSelfGeneralSettings />
                    </Tabs.TabPane>
                    <Tabs.TabPane
                        key="UserSelfAuthenticatorSettings"
                        tab={getAuthenticatorTabTitle(t, user.isAuthenticatorEnabled)}
                        destroyInactiveTabPane
                    >
                        <UserSelfAuthenticatorSettings />
                    </Tabs.TabPane>
                    <Tabs.TabPane
                        key="UserSelfPasswordSettings"
                        tab={getPasswordTabTitle(t, user)}
                        destroyInactiveTabPane
                    >
                        <UserSelfPasswordSettings />
                    </Tabs.TabPane>
                    {mayWebAuthn && (
                        <Tabs.TabPane
                            key="UserSelfWebAuthnSettings"
                            tab={t('userSelf:webAuthnSettings.title')}
                            destroyInactiveTabPane
                        >
                            <UserSelfWebAuthnSettings />
                        </Tabs.TabPane>
                    )}
                    <Tabs.TabPane key="UserSelfSessions" tab={t('userSelf:sessions.title')} destroyInactiveTabPane>
                        <UserSelfSessions />
                    </Tabs.TabPane>
                </Tabs>
            </Card>
        </ConsolePageWithHeader>
    );
};

export default UserSelfPage;
